import { useEffect, useRef } from 'react';
import { Client } from '@stomp/stompjs';
import SockJS from 'sockjs-client';
import { HOST_API_KEY } from '../config-global';

export interface AgentRequestPayload {
  type: 'NEW' | 'TAKEN' | 'CANCELLED';
  orderId: number;
  sessionId: string;
  transcript?: string;
  requestedAt?: string;
}

export interface AgentConnectedPayload {
  orderId: number;
  sessionId: string;
  agentId: string;
  agentName: string;
}

interface UseVoiceAgentSocketOptions {
  subOrgId: string | undefined;
  sessionId?: string;
  onAgentRequest?: (payload: AgentRequestPayload) => void;
  onAgentConnected?: (payload: AgentConnectedPayload) => void;
  onAgentReleased?: () => void;
  onConversationSnapshot?: (sessionId: string, steps: any[]) => void;
}

export function useVoiceAgentSocket({
  subOrgId,
  sessionId,
  onAgentRequest,
  onAgentConnected,
  onAgentReleased,
  onConversationSnapshot,
}: UseVoiceAgentSocketOptions) {
  const clientRef = useRef<Client | null>(null);
  const onAgentRequestRef = useRef(onAgentRequest);
  const onAgentConnectedRef = useRef(onAgentConnected);
  const onAgentReleasedRef = useRef(onAgentReleased);
  const onConversationSnapshotRef = useRef(onConversationSnapshot);

  useEffect(() => { onAgentRequestRef.current = onAgentRequest; }, [onAgentRequest]);
  useEffect(() => { onAgentConnectedRef.current = onAgentConnected; }, [onAgentConnected]);
  useEffect(() => { onAgentReleasedRef.current = onAgentReleased; }, [onAgentReleased]);
  useEffect(() => { onConversationSnapshotRef.current = onConversationSnapshot; }, [onConversationSnapshot]);

  useEffect(() => {
    if (!subOrgId) return undefined;

    const socketUrl = `${(HOST_API_KEY || 'http://localhost:8080').replace(/\/$/, '')}/ws`;

    const client = new Client({
      webSocketFactory: () => new SockJS(socketUrl),
      connectHeaders: {
        Authorization: `Bearer ${localStorage.getItem('accessToken') || ''}`,
      },
      reconnectDelay: 5000,
      onConnect: () => {
        client.subscribe(`/topic/agent-requests/${subOrgId}`, (message) => {
          try {
            const data: AgentRequestPayload = JSON.parse(message.body);
            onAgentRequestRef.current?.(data);
            // Subscribe to the conversation snapshot for this specific session
            if (data.type === 'NEW' && data.sessionId) {
              client.subscribe(`/topic/conversation-snapshot/${data.sessionId}`, (snapshotMsg) => {
                try {
                  const steps = JSON.parse(snapshotMsg.body);
                  onConversationSnapshotRef.current?.(data.sessionId, steps);
                } catch (e) {
                  console.warn('[VoiceAgentSocket] Snapshot parse error:', e);
                }
              });
            }
          } catch (e) {
            console.warn('[VoiceAgentSocket] Parse error:', e);
          }
        });

        if (sessionId) {
          client.subscribe(`/topic/agent-connected/${sessionId}`, (message) => {
            try {
              const data: AgentConnectedPayload = JSON.parse(message.body);
              onAgentConnectedRef.current?.(data);
            } catch (e) {
              console.warn('[VoiceAgentSocket] Parse error:', e);
            }
          });

          client.subscribe(`/topic/agent-released/${sessionId}`, () => {
            onAgentReleasedRef.current?.();
          });
        }
      },
      onWebSocketError: (error) => {
        console.warn('[VoiceAgentSocket] WebSocket error:', error);
      },
    });

    client.activate();
    clientRef.current = client;

    return () => {
      client.deactivate();
      clientRef.current = null;
    };
  }, [subOrgId, sessionId]);
}
